import { useId } from "react";

import type { SmeCardView } from "../../../api/types";
import { Button } from "../../../ui/Button";
import { anythingWaits, elementWaits } from "../shared/waits";
import { validateGate } from "./decide";
import type { AnswerKind } from "./decide";

interface DecisionBarProps {
  card: SmeCardView;
  busy: boolean;
  /** Showing the card is being recorded. */
  showing: boolean;
  /** A line in plain words when "Show the card" was refused. */
  showError: string | null;
  onShow: () => void;
  onValidate: () => void;
  onAnswer: (kind: AnswerKind) => void;
}

/**
 * Validate, Send back (comment required), Reject (reason required) and Note, which changes no state. Validate stays off
 * until the card was displayed and nothing blocks it. Note is open on a blocked card and on a card that is locked.
 */
export function DecisionBar({ card, busy, showing, showError, onShow, onValidate, onAnswer }: DecisionBarProps) {
  const hintId = useId();
  const lines = [...card.library_lines, ...card.lines];
  const waits = elementWaits(card.element);
  const gate = validateGate(card);
  const mayNote = anythingWaits(card.element, lines);

  if (!waits && !mayNote) {
    return (
      <div className="ra-decisions" role="group" aria-label="Your answer">
        <p className="muted">Nothing on this element is waiting for you.</p>
      </div>
    );
  }

  const needsShowing = waits && !card.displayed && card.blockers.length === 0;
  return (
    <div className="ra-decisions" role="group" aria-label="Your answer">
      <div className="row">
        {waits && (
          <Button variant="primary" onClick={onValidate} disabled={!gate.open || busy} aria-describedby={gate.reason ? hintId : undefined}>
            Validate
          </Button>
        )}
        {waits && (
          <Button onClick={() => onAnswer("send_back")} disabled={busy}>
            Send back
          </Button>
        )}
        {waits && (
          <Button onClick={() => onAnswer("reject")} disabled={busy}>
            Reject
          </Button>
        )}
        {mayNote && (
          <Button onClick={() => onAnswer("note")} disabled={busy}>
            Note
          </Button>
        )}
        {needsShowing && (
          <Button variant="quiet" onClick={onShow} busy={showing}>
            Show the card
          </Button>
        )}
      </div>
      {gate.reason && waits && (
        <p id={hintId} className="ra-decisions__hint muted">
          {gate.reason}
        </p>
      )}
      {showError && (
        <p role="alert" className="form-error">
          {showError}
        </p>
      )}
    </div>
  );
}
